import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { UserService } from './user.service';
import { LoggerService } from './logger.service';

/**
 * Owns the user's language preference.
 *
 * Source of truth is the logged-in user's `preferred_langcode` on their Drupal
 * User entity, surfaced via test_login/user_login (AuthService.current_user) and
 * persisted via UserService.savePreferredLanguage(). localStorage is kept as a
 * fast cache so the language is applied instantly on startup (before login
 * resolves) and on the pre-auth login screen, avoiding a flash of the default
 * language. A language chosen on the pre-auth login screen is held as a pending
 * preference and persisted to the account on login (syncPendingLanguage).
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  /** Languages offered in the footer selector / supported i18n bundles. */
  readonly supportedLanguages = ['en', 'fr', 'es', 'it', 'de', 'zh', 'ar'];
  readonly defaultLanguage = 'en';

  /** localStorage key holding a language chosen pre-auth, pending sync to the account on login. */
  private static readonly PENDING_KEY = 'language_pending';

  constructor(
    private _translate: TranslateService,
    private _authService: AuthService,
    private _storage: StorageService,
    private _userRESTService: UserService,
  ) {}

  /** The language to use before the current user is known (cache, then default). */
  getCachedLanguage(): string {
    const cached = this._storage.getItem('language');
    return this.isSupported(cached) ? cached : this.defaultLanguage;
  }

  /** Apply a language to the UI and update the localStorage cache. Does not persist to the user. */
  applyLanguage(langcode: string): void {
    const lang = this.isSupported(langcode) ? langcode : this.defaultLanguage;
    this._translate.use(lang);
    this._storage.setItem('language', lang);
  }

  /**
   * Apply the logged-in user's language once current_user is populated (call on
   * login success and on app-load test_login). Precedence:
   *   1. a language picked on the pre-auth login screen (synced + persisted), else
   *   2. the user's saved account preference, else
   *   3. the cached/default language (when the account has no saved preference).
   */
  applyUserLanguage(): void {
    if (this.syncPendingLanguage()) {
      return; // Pre-auth choice wins and was persisted to the account.
    }
    const saved = this._authService.current_user?.['preferred_langcode'];
    this.applyLanguage(this.isSupported(saved) ? saved : this.getCachedLanguage());
  }

  /**
   * Change the language: apply it immediately, then persist to the user entity
   * (and keep AuthService.current_user in sync) when a user is logged in.
   */
  changeLanguage(langcode: string): void {
    if (!this.isSupported(langcode)) {
      return;
    }
    this.applyLanguage(langcode);

    const uid = this._authService.current_user?.['uid'];
    if (!uid) {
      // Pre-auth (login screen): remember the choice as a pending preference so
      // it gets persisted to the account once the user logs in (see syncPendingLanguage).
      this._storage.setItem(LanguageService.PENDING_KEY, langcode);
      return;
    }

    this.persistLanguage(langcode);
  }

  /**
   * After login, persist a language the user picked on the pre-auth login screen
   * to their account, so a logged-out selection isn't silently lost. Call this
   * once current_user is populated. Returns true if a pending choice was applied.
   */
  syncPendingLanguage(): boolean {
    const pending = this._storage.getItem(LanguageService.PENDING_KEY);
    this._storage.removeItem(LanguageService.PENDING_KEY);

    const uid = this._authService.current_user?.['uid'];
    if (!uid || !this.isSupported(pending)) {
      return false;
    }
    const saved = this._authService.current_user?.['preferred_langcode'];
    if (pending === saved) {
      return false; // Already matches the account; nothing to do.
    }
    this.applyLanguage(pending);
    this.persistLanguage(pending);
    return true;
  }

  /** Persist a language to the current user's account and keep current_user in sync. */
  private persistLanguage(langcode: string): void {
    this._userRESTService.savePreferredLanguage(langcode).subscribe({
      next: () => {
        // Keep the in-memory user in sync so a later applyUserLanguage() agrees.
        if (this._authService.current_user) {
          this._authService.current_user['preferred_langcode'] = langcode;
        }
      },
      error: (error) => {
        LoggerService.error('Failed to save preferred language:', error);
      },
    });
  }

  private isSupported(langcode: any): boolean {
    return typeof langcode === 'string' && this.supportedLanguages.includes(langcode);
  }
}
